import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import TableViewLayout from 'layouts/TableViewLayout';
import { useAppSelector } from 'redux/store';

import { fetchEventInstanceHistory } from 'redux/slices/eventInstanceHistorySlice';
import type { EventInstanceHistoryRow } from 'api/event_instances/endpoints/getEventInstanceHistory';

/**
 * Newest first, which is the only useful opening for a page answering "what
 * happened". Without it the fallback is the first header_key ascending — zone
 * name — which alphabetises the list and interleaves old with new.
 *
 * Module-level so its identity is stable across renders.
 */
const DEFAULT_SORT = { column: 'created_at', order: 'desc' } as const;

/**
 * Event History: process and file occurrences that are no longer outstanding.
 *
 * Read-only by design. Its counterpart, Live Events, is where anything is
 * steered; this page answers "what happened", and the boundary between them is
 * consumption.
 *
 * The two kinds share one table rather than being null-padded across each
 * other's columns: the single genuinely kind-specific column is merged into
 * `description` by the backend (a raise comment for a process occurrence, a
 * path for a file arrival).
 */
const EventHistory = () => {
  const userData = useAppSelector((state) => state.auth.userData);
  const { instances, config: headerConfig, filtersConfig, pagination, error, loading } = useAppSelector((state) => state.eventInstanceHistory);

  const isLoading = loading?.fetchEventInstanceHistory ?? false;

  const pageName = 'event-history';
  const [showSidebar, setShowSidebar] = useState(false);

  // `event_id` scopes the page; it is not a filter the operator set. It is
  // declared as an extra filter so the panel can show and clear it, but
  // filtersConfig arrives IN the response — so on a cold visit to
  // ?event_id=7 the first fetch would be hydrated before the config existed
  // and carry no event_id at all, rendering every event's history for a beat
  // before correcting itself. Reading it straight from the URL scopes that
  // first request too.
  const [searchParams] = useSearchParams();
  const scopedEventId = searchParams.get('event_id');

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    // Once filtersConfig has loaded, the payload carries event_id itself and
    // the two agree; this only matters for the request that precedes it.
    fetchData: (payload: Record<string, unknown>) => fetchEventInstanceHistory(scopedEventId === null ? payload : { ...payload, event_id: scopedEventId }),
    defaultSort: DEFAULT_SORT,
    // finished_at is null for every file row and for process rows cleared
    // before the column existed, so the formatter has to tolerate null — it
    // means "unknown", never "file kind".
    columnFormats: { created_at: 'datetime', finished_at: 'datetime' },
    // The event name links to its Live Events card. Declared as a cell on the
    // generated column rather than as a replacement column, so it stays in the
    // column chooser, in the user's saved order, and sortable -- event_name is
    // one of the backend's sortable columns.
    columnCells: {
      event_name: (row: EventInstanceHistoryRow) => <Link to={`/automation/live_events?focus=${row.event_id}`}>{row.event_name}</Link>,
    },
    getFormattedRow: (row: EventInstanceHistoryRow) => ({
      ...row,
      // The backend returns the actors as an array; the table renders scalars.
      actors: row.actors.join(', '),
    }),
  });

  const columns = generateColumns((key, order) => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });

  // Keyed on the rows alone, as every other list page is. `getFormattedData` is
  // rebuilt on each render by useTableController, so including it recomputes the
  // memo every render and hands the table a new `data` identity -- which a sort
  // or filter click turns into a render loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const formattedData = useMemo(() => getFormattedData(instances), [instances]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    // occurrence_id is unique only within a kind, so the row key needs both.
    getRowId: (row: EventInstanceHistoryRow) => `${row.kind}-${row.occurrence_id}`,
  });

  return (
    <TableViewLayout
      filtersConfig={filtersConfig}
      filters={filters}
      pageName={pageName}
      columns={headerConfig.map((column) => ({ key: column.header_key, label: column.label }))}
      pageSize={limit}
      error={error}
      onFilterChange={setFilterAndSync}
      onResetFilters={resetFilters}
      onPageSizeChange={updateLimit}
      currentPage={pagination.currentPage}
      totalPages={pagination.totalPages}
      totalItems={pagination.totalItems}
      onPageChange={updatePage}
      table={table}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      isLoading={isLoading}
      onRefresh={refresh}
      isSilentRefresh={isSilentRefresh}
    />
  );
};

export default EventHistory;
